import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, History, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { getWhatsAppConsent, setWhatsAppConsent } from '@/services/whatsappMessaging.service';
import {
  getMedicationHistoryConsent,
  setMedicationHistoryConsent,
} from '@/services/prescriptions.service';
import {
  Button,
  DatePicker,
  FormField,
  Input,
  Modal,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spinner,
} from '@/components/ui';
import { DialCodeSelect } from '@/components/ui/DialCodeSelect';
import { formatPhoneAsYouType, formatPhoneByCode, normalizeDialCode } from '@/lib/phone';
import { usePatientPhoto } from '@/hooks/usePatientPhoto';
import { patientFieldLimitErrors, patientFieldLimitSummary } from '@/components/scriptsure/patientFieldLimits';
import { getErrorMessage } from '@/services/apiClient';
import { listLanguages, type Language } from '@/services/admin.service';
import {
  checkCredentials,
  getDemographics,
  linkPatientToMaster,
  getPatient,
  listCountryCodes,
  listPatientFacilities,
  updatePatient,
  upsertDemographics,
  uploadPhoto,
  type DemographicsHistoryEntry,
  type MasterConflictInfo,
  type PatientDemographics,
  type PatientRecord,
  type UpdatePatientPayload,
  type UpsertDemographicsPayload,
} from '@/services/patients.service';
import type { SelectOption } from '@/types/common.types';

type BinaryChoice = '' | '1' | '0';

interface FormState {
  firstName: string;
  middleName: string;
  lastName: string;
  maidenName: string;
  nickname: string;
  gender: string;
  birthDate: string;
  email: string;
  emailOptIn: BinaryChoice;
  marketingEmails: BinaryChoice;
  address: string;
  city: string;
  state: string;
  zip: string;
  countryID: string;
  homePhone: string;
  /** The home phone's dial code ('44'), never a countryID. */
  phoneCountryCode: string;
  cellPhone: string;
  /** The cell's dial code ('44'), never a countryID. */
  cellCountryCode: string;
  smsOptIn: BinaryChoice;
  marketingSms: BinaryChoice;
  languageId: string;
  interpreterNeeded: BinaryChoice;
  chartId: string;
  facilityID: string;
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (val: boolean) => void; label?: string }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-emerald-500' : 'bg-slate-300'
        }`}
      >
        <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[18px]' : 'translate-x-[3px]'
        }`} />
      </button>
      {label && <span className="text-sm text-slate-700 select-none">{label}</span>}
    </label>
  );
}

function formatHistoryDate(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function DemographicsHistoryPopover({
  entries,
  onApply,
  formatValue,
}: {
  entries: DemographicsHistoryEntry[];
  onApply: (value: string) => void;
  formatValue?: (value: string) => string;
}) {
  if (entries.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="xs" leftIcon={<History className="h-3.5 w-3.5" />}>
          History
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-80 max-h-72 overflow-y-auto p-3">
        <div className="space-y-1.5">
          {entries.map((entry, index) => {
            const author = entry.userName;
            const display = formatValue ? formatValue(entry.value) : entry.displayValue;
            return (
              <button
                key={`${entry.createdAt ?? 'unknown'}-${index}`}
                type="button"
                onClick={() => onApply(entry.value)}
                className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-50"
              >
                <div className="text-sm font-medium text-slate-800">
                  {display.trim() !== '' ? display : '(Blank value)'}
                </div>
                <div className="text-xs text-slate-500">
                  {formatHistoryDate(entry.createdAt)}
                  {author ? ` by ${author}` : ''}
                  {entry.viaMaster ? (
                    <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-500">
                      via shared record
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** '1'/'0' answers read as On/Off in the history list, not as bare digits. */
const onOff = (value: string): string => (value === '1' ? 'On' : value === '0' ? 'Off' : value);

/**
 * The unsubscribe answers are stored with the opposite polarity to the
 * "Marketing" toggles that display them, so both the label and the value
 * applied on click have to be flipped.
 */
const subscribedLabel = (value: string): string =>
  value === '1' ? 'Unsubscribed' : value === '0' ? 'Subscribed' : value;

function computeAge(birthDate: string): number | null {
  if (!birthDate || birthDate.startsWith('0000')) return null;
  const dob = new Date(birthDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return null;
  return Math.floor((Date.now() - dob.getTime()) / 31557600000);
}

const GENDER_OPTIONS: SelectOption<string>[] = [
  { value: '', label: 'Select...' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

function toNullableString(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function toNullableNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function toBinaryChoice(value: unknown, fallback: BinaryChoice = '0'): BinaryChoice {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value ? '1' : '0';
  if (typeof value === 'number') return value !== 0 ? '1' : '0';
  const normalized = String(value).trim().toLowerCase();
  if (normalized === '1' || normalized === 'true' || normalized === 'yes') return '1';
  if (normalized === '0' || normalized === 'false' || normalized === 'no') return '0';
  return fallback;
}

function withCurrentOption(
  options: SelectOption<string>[],
  value: string,
  label: string,
): SelectOption<string>[] {
  if (!value) return options;
  if (options.some((option) => option.value === value)) return options;
  return [{ value, label }, ...options];
}

function normalizeGender(value: unknown): string {
  if (!value) return '';
  const s = String(value).trim();
  if (s === 'M' || s === 'Male' || s === 'male') return 'M';
  if (s === 'F' || s === 'Female' || s === 'female') return 'F';
  if (s) return 'Other';
  return '';
}

function buildInitialForm(patient: PatientRecord, demographics: PatientDemographics | null): FormState {
  // `Patient.address` is typed as a structured PatientAddress, but this endpoint
  // sends the flat v4 column. See the note on PatientRecord's flat fields.
  const flatAddress = (patient as unknown as { address?: string | null }).address;
  const rawFields = demographics?.rawFields ?? {};

  const nickname = demographics?.nickname ?? rawFields['nickname'] ?? patient.informalName ?? '';
  const chartId = demographics?.chartId ?? rawFields['chart_id'] ?? '';
  const languageRaw = demographics?.languageId ?? rawFields['language'];
  const languageId = languageRaw != null && String(languageRaw).trim() !== '' ? String(languageRaw) : '';
  const interpreterRaw = demographics?.interpreterNeeded ?? rawFields['interpreterNeeded'];

  let interpreterNeeded: BinaryChoice = '';
  if (interpreterRaw != null && String(interpreterRaw).trim() !== '') {
    interpreterNeeded = toBinaryChoice(interpreterRaw, '');
  }

  return {
    firstName: patient.firstName ?? '',
    middleName: patient.middleName ?? '',
    lastName: patient.lastName ?? '',
    maidenName: patient.maidenName ?? '',
    nickname: String(nickname),
    gender: normalizeGender(patient.gender),
    birthDate: String(patient.birthDate ?? patient.dateOfBirth ?? ''),
    email: patient.email ?? '',
    emailOptIn: toBinaryChoice(patient.emailOptIn, '0'),
    marketingEmails: toBinaryChoice(!(patient.emailUnsubscribe ?? false), '0'),
    address: String(flatAddress ?? ''),
    city: patient.city ?? '',
    state: patient.state ?? '',
    zip: String(patient.zip ?? ''),
    countryID: patient.countryID != null ? String(patient.countryID) : '1',
    homePhone: (patient.homePhone ?? '').replace(/\D/g, ''),
    phoneCountryCode: normalizeDialCode(patient.phoneCountryCode),
    cellPhone: (patient.cellPhone ?? '').replace(/\D/g, ''),
    cellCountryCode: normalizeDialCode(patient.cellCountryCode),
    smsOptIn: toBinaryChoice(patient.smsOptIn, '0'),
    marketingSms: toBinaryChoice(!(patient.smsUnsubscribe ?? false), '0'),
    languageId,
    interpreterNeeded,
    chartId: String(chartId),
    facilityID: patient.facilityID != null ? String(patient.facilityID) : '',
  };
}

/**
 * The rule staff cannot guess, in their own words.
 *
 * WhatsApp is not like text or email: a practice may only write freely to a
 * patient for a day after that patient messages them. Without knowing that,
 * "agreed to be contacted on WhatsApp" is a toggle with no meaning, so the
 * explanation sits next to the decision rather than in a training document.
 */
const WHATSAPP_RULE =
  "WhatsApp only lets you write freely to a patient for a day after they message you. "
  + "After that you can only send one of your practice's ready-made messages, and only to "
  + 'patients who have said they are happy to be contacted on WhatsApp.';

interface PatientDemographicsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: number;
}

export function PatientDemographicsEditModal({
  isOpen,
  onClose,
  patientId,
}: PatientDemographicsEditModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [validating, setValidating] = useState(false);
  /**
   * A SHARED PATIENT — one identity held across accounts — that already owns the
   * email or phone being saved. Set by the pre-flight or by the server's 409;
   * cleared whenever a fresh save attempt starts, so a stale offer can never be
   * acted on. The variable keeps the API's `master` vocabulary; the wording it
   * renders in the footer never does.
   */
  const [masterConflict, setMasterConflict] = useState<MasterConflictInfo | null>(null);

  /**
   * WhatsApp consent. Read and written through its own endpoint, because it
   * lives in its own table rather than on the patient row — and that table is
   * APPEND-ONLY, which is why it is deliberately not part of the form payload
   * below. See the save handler.
   *
   * Gated on `explicit`, the fail-closed signal. `enabled` is fail-open and
   * true while loading, so it would fire a guaranteed 422 for every practice
   * that does not use WhatsApp.
   */
  const whatsAppOn = useModuleAccess('whatsapp').explicit;
  const consentQuery = useQuery({
    queryKey: ['whatsapp-consent', patientId],
    queryFn: () => getWhatsAppConsent(patientId),
    enabled: patientId > 0 && whatsAppOn,
    staleTime: 60_000,
    retry: false,
  });

  /**
   * Draft state for the toggle; null until someone touches it, so an untouched
   * modal writes nothing. That is what keeps an unrelated Save from appending a
   * consent row to an append-only table.
   */
  const [whatsAppDraft, setWhatsAppDraft] = useState<boolean | null>(null);
  /** The attestation. Starts unticked every time the modal opens. */
  const [whatsAppAffirmed, setWhatsAppAffirmed] = useState(false);
  const [whatsAppError, setWhatsAppError] = useState<string | null>(null);

  const whatsAppSaved = consentQuery.data?.optedIn ?? null;
  const whatsAppValue = whatsAppDraft ?? whatsAppSaved ?? false;
  const whatsAppChanged = whatsAppSaved !== null && whatsAppDraft !== null && whatsAppDraft !== whatsAppSaved;

  /**
   * Medication-history consent: whether the patient agreed to let the practice
   * download their medication history from pharmacies and insurers through
   * ScriptSure. Kept like WhatsApp consent above: its own endpoint, an
   * APPEND-ONLY table, and the same draft / attestation / error state below.
   *
   * Gated on `explicit` for the same fail-closed reason, and on `isOpen`:
   * unlike WhatsApp's, the demographics card does not read it too, so without
   * that every chart visit would fetch it for an editor nobody opened.
   */
  const dawOn = useModuleAccess('daw').explicit;
  const medicationHistoryQuery = useQuery({
    queryKey: ['medication-history-consent', patientId],
    queryFn: () => getMedicationHistoryConsent(patientId),
    enabled: isOpen && patientId > 0 && dawOn,
    staleTime: 60_000,
    retry: false,
  });

  // ScriptSure's length limits, flagged as staff type and enforced on Save.
  // DAW accounts only: everyone else keeps the columns' own limits. Keyed on
  // `explicitEvenIfStale`, not `dawOn`: enforcement fails toward on while the
  // modules data is only the stored copy or its latest refresh failed.
  // Derived from the form on every render and never copied into fieldErrors,
  // so a value shortened any way (typing, History) clears its message at once.
  const dawLimitsOn = useModuleAccess('daw').explicitEvenIfStale;
  const limitErrors = dawLimitsOn && form ? patientFieldLimitErrors(form) : {};
  const limitSummary = patientFieldLimitSummary(limitErrors);

  const [medicationHistoryDraft, setMedicationHistoryDraft] = useState<boolean | null>(null);
  const [medicationHistoryAffirmed, setMedicationHistoryAffirmed] = useState(false);
  const [medicationHistoryError, setMedicationHistoryError] = useState<string | null>(null);

  const medicationHistorySaved = medicationHistoryQuery.data?.consented ?? null;
  const medicationHistoryValue = medicationHistoryDraft ?? medicationHistorySaved ?? false;
  const medicationHistoryChanged =
    medicationHistorySaved !== null
    && medicationHistoryDraft !== null
    && medicationHistoryDraft !== medicationHistorySaved;

  // A consent edit belongs to the open it was made in. The chart keeps this
  // editor mounted between opens, so both consents' drafts and attestations
  // are cleared on close: otherwise a grant ticked, or a withdrawal switched
  // off, before a Cancel would still be pending at the next open, and a later
  // Save made for an unrelated field would record it. After a completed save
  // these are already cleared, so this only changes the Cancel path.
  useEffect(() => {
    if (isOpen) return;
    setWhatsAppDraft(null);
    setWhatsAppAffirmed(false);
    setWhatsAppError(null);
    setMedicationHistoryDraft(null);
    setMedicationHistoryAffirmed(false);
    setMedicationHistoryError(null);
  }, [isOpen]);

  const detailQuery = useQuery({
    queryKey: ['patient-info-demographics', patientId],
    queryFn: async () => {
      const [patient, demographics] = await Promise.all([
        getPatient(patientId),
        getDemographics(patientId),
      ]);
      return { patient, demographics };
    },
    enabled: isOpen,
  });

  const facilityQuery = useQuery({
    queryKey: ['patient-info-demographics-facilities'],
    queryFn: listPatientFacilities,
    enabled: isOpen,
  });

  const countryQuery = useQuery({
    queryKey: ['patient-info-demographics-country-codes'],
    queryFn: listCountryCodes,
    enabled: isOpen,
  });

  const languageQuery = useQuery({
    queryKey: ['patient-info-demographics-languages'],
    queryFn: listLanguages,
    enabled: isOpen,
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    setForm(buildInitialForm(detailQuery.data.patient, detailQuery.data.demographics));
    setSaveFeedback(null);
    setFieldErrors({});
    setMasterConflict(null);
  }, [detailQuery.data]);

  /**
   * The consent writes, one per consent, run by the save below after the form
   * is saved and only for a consent that changed. Each has its own error
   * handling and resolves to whether it saved, so a failure shows beside its
   * own toggle and nowhere else.
   *
   * A write that succeeds settles its consent at once: the cache takes the
   * server's answer and the draft clears, so it stops reading as changed and
   * the next Save leaves it alone. That next Save may come straight away, to
   * retry the other consent, before any reread has landed.
   *
   * A failure message also says the details saved. The footer's "Changes
   * saved." cannot be relied on for that while the editor stays open: the
   * save's own reread of the patient hands the form new data, and the effect
   * above that rebuilds the form clears the footer message with it.
   */
  async function saveWhatsAppConsent(): Promise<boolean> {
    setWhatsAppError(null);
    try {
      // The third argument is the affirmation, and it carries the ticked
      // checkbox rather than being hardcoded true -- passing `true` here
      // regardless would make the server-side requirement decorative.
      // Withdrawing needs no affirmation, which is why it is not required
      // when turning off.
      const saved = await setWhatsAppConsent(patientId, whatsAppDraft === true, whatsAppAffirmed);
      queryClient.setQueryData(['whatsapp-consent', patientId], saved);
      setWhatsAppDraft(null);
      setWhatsAppAffirmed(false);
      return true;
    } catch {
      setWhatsAppError(
        "The patient's details were saved, but the WhatsApp consent wasn't. Try again, and contact support if it keeps happening.",
      );
      return false;
    }
  }

  async function saveMedicationHistoryConsent(): Promise<boolean> {
    setMedicationHistoryError(null);
    try {
      // Carries the ticked confirmation, for the same reason as WhatsApp's.
      const saved = await setMedicationHistoryConsent(
        patientId,
        medicationHistoryDraft === true,
        medicationHistoryAffirmed,
      );
      queryClient.setQueryData(['medication-history-consent', patientId], saved);
      setMedicationHistoryDraft(null);
      setMedicationHistoryAffirmed(false);
      return true;
    } catch {
      setMedicationHistoryError(
        "The patient's details were saved, but the medication history consent wasn't. Try again, and contact support if it keeps happening.",
      );
      return false;
    }
  }

  const saveMutation = useMutation({
    mutationFn: async (draft: FormState) => {
      const patientPayload: UpdatePatientPayload = {
        firstName: draft.firstName.trim(),
        middleName: toNullableString(draft.middleName),
        lastName: draft.lastName.trim(),
        maidenName: toNullableString(draft.maidenName),
        email: toNullableString(draft.email),
        birthDate: toNullableString(draft.birthDate),
        gender: toNullableString(draft.gender),
        address: toNullableString(draft.address),
        city: toNullableString(draft.city),
        state: toNullableString(draft.state),
        zip: toNullableString(draft.zip),
        countryID: toNullableNumber(draft.countryID),
        homePhone: toNullableString(draft.homePhone.replace(/\D/g, '')),
        cellPhone: toNullableString(draft.cellPhone.replace(/\D/g, '')),
        emailOptIn: draft.emailOptIn === '1',
        smsOptIn: draft.smsOptIn === '1',
        facilityID: toNullableNumber(draft.facilityID),
        // The dial codes only. The API writes the country IDs from them.
        phoneCountryCode: normalizeDialCode(draft.phoneCountryCode),
        cellCountryCode: normalizeDialCode(draft.cellCountryCode),
        emailUnsubscribe: draft.marketingEmails === '0',
        smsUnsubscribe: draft.marketingSms === '0',
      };

      const [birthYear, birthMonth, birthDay] = (draft.birthDate || '').split('-');

      const baseRawFields = { ...(detailQuery.data?.demographics?.rawFields ?? {}) };
      delete baseRawFields['nickname'];
      delete baseRawFields['chart_id'];
      delete baseRawFields['language'];
      delete baseRawFields['interpreterNeeded'];

      const demographicsPayload: UpsertDemographicsPayload = {
        nickname: toNullableString(draft.nickname),
        chartId: toNullableString(draft.chartId),
        languageId: toNullableNumber(draft.languageId),
        interpreterNeeded:
          draft.interpreterNeeded === '' ? null : draft.interpreterNeeded === '1',
        rawFields: {
          ...baseRawFields,
          first_name: draft.firstName.trim() || null,
          middle_name: toNullableString(draft.middleName),
          last_name: draft.lastName.trim() || null,
          maiden_name: toNullableString(draft.maidenName),
          gender: toNullableString(draft.gender),
          birth_month: birthMonth || null,
          birth_date: birthDay || null,
          birth_year: birthYear || null,
          email: toNullableString(draft.email),
          emailOptIn: draft.emailOptIn === '' ? null : draft.emailOptIn,
          // Stored as the UNSUBSCRIBE flag, which is the inverse of the
          // "Marketing Email" toggle — same polarity as the column it mirrors.
          emailUnsubscribe: draft.marketingEmails === '1' ? '0' : '1',
          address: toNullableString(draft.address),
          city: toNullableString(draft.city),
          state: toNullableString(draft.state),
          zip: toNullableString(draft.zip),
          country: draft.countryID === '' ? null : draft.countryID,
          home_phone: toNullableString(draft.homePhone.replace(/\D/g, '')),
          cell_phone: toNullableString(draft.cellPhone.replace(/\D/g, '')),
          smsOptIn: draft.smsOptIn === '' ? null : draft.smsOptIn,
          smsUnsubscribe: draft.marketingSms === '1' ? '0' : '1',
        },
      };

      await Promise.all([
        updatePatient(patientId, patientPayload),
        upsertDemographics(patientId, demographicsPayload),
      ]);

      // The consents are written AFTER, only when each actually changed, and
      // each in its own error handling (see saveWhatsAppConsent above).
      //
      // Deliberately not in the Promise.all above. Both consent tables are
      // append-only — every write inserts a row — so joining them would add a
      // fresh consent record every time anyone pressed Save on an unrelated
      // demographics edit, burying the audit trail the tables exist for. And
      // because each catches its own failure, a consent that fails cannot
      // report the whole save as failed: the form is already saved by here.
      const [whatsAppWritten, medicationHistoryWritten] = await Promise.all([
        whatsAppChanged ? saveWhatsAppConsent() : true,
        medicationHistoryChanged ? saveMedicationHistoryConsent() : true,
      ]);
      return { consentsSaved: whatsAppWritten && medicationHistoryWritten };
    },
    onSuccess: async ({ consentsSaved }) => {
      setSaveFeedback('Changes saved.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['patient-info-demographics', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patient', String(patientId)] }),
        queryClient.invalidateQueries({ queryKey: ['whatsapp-consent', patientId] }),
        ...(dawOn
          ? [
              queryClient.invalidateQueries({ queryKey: ['medication-history-consent', patientId] }),
              // The Prescriptions tab keeps its automatic open's answer for
              // 10 minutes and its context for the app default. Marking both
              // stale makes the tab ask the server again, so what it offers
              // (Prescribe, or the details still to fix) follows this save.
              queryClient.invalidateQueries({ queryKey: ['daw-session', patientId] }),
              queryClient.invalidateQueries({ queryKey: ['patient-prescriptions-context', patientId] }),
            ]
          : []),
      ]);
      // A consent that failed keeps the editor open, its message beside its
      // own toggle, so it can be saved again. The form itself is saved.
      if (consentsSaved) onClose();
    },
    onError: (error: unknown) => {
      // The save now refuses a duplicate or a value owned by another master
      // record. Put the server's per-field messages back on the fields rather
      // than showing one opaque banner, and surface the link offer when the
      // server says one is available.
      const detail = (error as { response?: { data?: { error?: { code?: string; details?: {
        errors?: Record<string, string>; master?: MasterConflictInfo | null;
      } } } } })?.response?.data?.error;
      const code = detail?.code;
      if (code === 'MASTER_PATIENT_MATCH' || code === 'DUPLICATE_CREDENTIALS') {
        setFieldErrors(detail?.details?.errors ?? {});
        setMasterConflict(detail?.details?.master ?? null);
        return;
      }
      setSaveFeedback(getErrorMessage(error));
    },
  });

  const linkMutation = useMutation({
    mutationFn: (conflict: MasterConflictInfo) =>
      linkPatientToMaster(patientId, {
        masterPatientId: conflict.masterPatientId,
        email: form?.email.trim(),
        cellPhone: form?.cellPhone.replace(/\D/g, ''),
      }),
    onSuccess: () => {
      // Linking only creates the link; the save still has to run, and now
      // cascades with the patient attached to the master.
      setMasterConflict(null);
      setFieldErrors({});
      if (form) saveMutation.mutate(form);
    },
    onError: (error: unknown) => setSaveFeedback(getErrorMessage(error)),
  });

  // The editor's own close, refused while a save is running, when Cancel is
  // disabled too. A consent that fails must reach the person who changed it,
  // in the open where they changed it. Closing mid-save would run the reset
  // above before the failure arrived, and the failure would then be waiting
  // beside the toggle at the next open, which made no such change. onSuccess
  // calls onClose itself, since the save still counts as running while
  // onSuccess runs.
  function handleClose() {
    if (saveMutation.isPending) return;
    onClose();
  }

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadPhoto(patientId, file),
    onSuccess: async () => {
      setSaveFeedback('Photo uploaded.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['patient-info-demographics', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patient', String(patientId)] }),
        queryClient.invalidateQueries({ queryKey: ['patient-photo', patientId] }),
        // The list caches `photoUrl: null` for a photo-less patient; refresh it so
        // the new avatar shows in the Patients list without a reload.
        queryClient.invalidateQueries({ queryKey: ['patients'] }),
      ]);
    },
  });

  const patientPhotoUrl = usePatientPhoto(
    patientId,
    detailQuery.data?.patient.photoUrl,
  );

  const facilityOptions = useMemo<SelectOption<string>[]>(() => {
    const base = (facilityQuery.data ?? []).filter((facility) => facility.active).map((facility) => ({
      value: String(facility.facilityId),
      label: facility.facilityName,
    }));
    const currentValue = form?.facilityID ?? '';
    const currentLabel = detailQuery.data?.patient.facilityName ?? 'Current facility';
    return withCurrentOption([{ value: '', label: 'Select...' }, ...base], currentValue, currentLabel);
  }, [detailQuery.data?.patient.facilityName, facilityQuery.data, form?.facilityID]);

  const countryOptions = useMemo<SelectOption<string>[]>(() => {
    const base = (countryQuery.data ?? []).map((country) => ({
      value: String(country.countryID),
      label: country.countryName,
      // Findable by ISO code too — "CH" is faster to type than "Switzerland".
      searchText: `${country.countryName} ${country.countryAbbr ?? ''}`.trim(),
    }));
    const currentValue = form?.countryID ?? '';
    const currentLabel = detailQuery.data?.patient.countryName ?? 'Current country';
    return withCurrentOption([{ value: '', label: 'Select...' }, ...base], currentValue, currentLabel);
  }, [countryQuery.data, detailQuery.data?.patient.countryName, form?.countryID]);

  /** The as-you-type field's hint: the US mask for +1, nothing for other codes. */
  const phonePlaceholder = (code: string): string => (code === '1' ? '(###) ###-####' : '');

  /**
   * The conflicting shared patient's name, for the footer notice. Blank when an
   * older API build sent no name — the notice then reads as it always did,
   * rather than showing a stray separator.
   */
  const sharedPatientName = (conflict: MasterConflictInfo): string =>
    `${conflict.firstName ?? ''} ${conflict.lastName ?? ''}`.trim();

  /**
   * That record's cell phone, formatted with THIS patient's cell dial code —
   * the conflict carries no code of its own, and a phone match compares codes,
   * so it is the same one.
   */
  const sharedPatientPhone = (conflict: MasterConflictInfo): string =>
    formatPhoneByCode(conflict.cellPhone, form?.cellCountryCode);

  const languageOptions = useMemo<SelectOption<string>[]>(() => {
    const base = (languageQuery.data ?? []).map((language: Language) => ({
      value: String(language.languageId),
      label: language.name,
    }));
    const currentValue = form?.languageId ?? '';
    const currentLabel =
      languageQuery.data?.find((language) => String(language.languageId) === currentValue)?.name ??
      (currentValue ? `Current language (${currentValue})` : '');
    return withCurrentOption([{ value: '', label: 'Select...' }, ...base], currentValue, currentLabel);
  }, [form?.languageId, languageQuery.data]);

  const initials = useMemo(() => {
    const first = detailQuery.data?.patient.firstName?.[0] ?? '';
    const last = detailQuery.data?.patient.lastName?.[0] ?? '';
    return `${first}${last}`.toUpperCase() || 'PT';
  }, [detailQuery.data?.patient.firstName, detailQuery.data?.patient.lastName]);

  const fieldHistory = detailQuery.data?.demographics?.fieldHistory ?? {};

  const historyFor = (questionKey: string): DemographicsHistoryEntry[] =>
    fieldHistory[questionKey] ?? [];

  const dobHistory = useMemo<DemographicsHistoryEntry[]>(() => {
    const months = fieldHistory['birth_month'] ?? [];
    const days = fieldHistory['birth_date'] ?? [];
    const years = fieldHistory['birth_year'] ?? [];
    const len = Math.max(months.length, days.length, years.length);
    if (len === 0) return [];

    const entries: DemographicsHistoryEntry[] = [];
    for (let i = 0; i < len; i++) {
      const m = months[i]?.value ?? '';
      const d = days[i]?.value ?? '';
      const y = years[i]?.value ?? '';
      if (!m && !d && !y) continue;
      const display = `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
      const source = months[i] ?? days[i] ?? years[i];
      entries.push({
        value: `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
        displayValue: display,
        createdAt: source.createdAt,
        userId: source.userId,
        userName: source.userName,
      });
    }
    return entries;
  }, [fieldHistory]);

  const formatLanguageValue = useMemo(() => {
    const langs = languageQuery.data ?? [];
    return (value: string) => {
      const match = langs.find((l) => String(l.languageId) === value);
      return match?.name ?? value;
    };
  }, [languageQuery.data]);

  const formatInterpreterValue = (value: string) =>
    value === '1' ? 'Yes' : value === '0' ? 'No' : value;

  const loading = detailQuery.isLoading || !form;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Demographics"
      size="xl"
      footer={
        /* The shared-patient conflict sits IN the footer, above the actions, and
           the footer is outside the modal's scroll area — so the refusal, the
           record it is about and the button that resolves it are all on screen
           together no matter where the form is scrolled. It used to render
           between the phone fields and Facility, which on a long form meant a
           save could appear to do nothing at all. */
        <div className="flex w-full flex-col gap-3">
          {masterConflict && (
            <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-amber-900">
                  {masterConflict.matchedOn.includes('email') && masterConflict.matchedOn.includes('phone')
                    ? 'This email and phone belong to a shared patient from another account'
                    : masterConflict.matchedOn.includes('email')
                      ? 'This email belongs to a shared patient from another account'
                      : 'This phone number belongs to a shared patient from another account'}
                </p>
                {/* Name first: it is what tells "the same person" from "a
                    mistyped address" at a glance. The contact values beside it
                    are that record's own, and one of them is what staff just
                    typed. Nothing here says which accounts hold that person. */}
                <p className="mt-0.5 truncate text-xs text-amber-800">
                  {[sharedPatientName(masterConflict), masterConflict.email, sharedPatientPhone(masterConflict)]
                    .filter((part) => part !== '')
                    .join(' · ')}
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  {masterConflict.canLink
                    ? 'If this is the same person, link this patient to that record and save. Otherwise use a different email or phone number.'
                    : 'This patient cannot take that record’s details. Use a different email or phone number.'}
                </p>
              </div>
              {masterConflict.canLink && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="shrink-0"
                  loading={linkMutation.isPending}
                  // Linking saves straight after, so it must refuse what Save refuses.
                  disabled={limitSummary !== null}
                  onClick={() => {
                    if (limitSummary) return;
                    linkMutation.mutate(masterConflict);
                  }}
                >
                  Link and save
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            {limitSummary && <p role="alert" className="text-xs text-red-600">{limitSummary}</p>}
            {saveFeedback && <p className="text-xs text-emerald-600">{saveFeedback}</p>}
            {saveMutation.isError && (
              <p className="text-xs text-red-600">
                {getErrorMessage(saveMutation.error, 'Failed to save.')}
              </p>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={handleClose} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              loading={saveMutation.isPending || validating}
              disabled={loading}
              onClick={async () => {
                if (!form) return;
                setSaveFeedback(null);
                setFieldErrors({});
                setMasterConflict(null);

                const localErrors: Record<string, string> = {};
                if (!form.firstName.trim()) {
                  localErrors.firstName = 'First name is required.';
                }
                if (!form.lastName.trim()) {
                  localErrors.lastName = 'Last name is required.';
                }
                if (!form.email.trim()) {
                  localErrors.email = 'Email is required.';
                }
                if (!form.cellPhone.replace(/\D/g, '')) {
                  localErrors.cellPhone = 'Cell phone is required.';
                }
                if (Object.keys(localErrors).length > 0) {
                  setFieldErrors(localErrors);
                  return;
                }
                // Each over-length field already shows its message, and the
                // footer names them all where it is always in view.
                if (limitSummary) return;

                // Turning a consent on records a named person's word on the
                // patient's record, so it needs the affirmation ticked. Refused
                // here rather than silently dropped, so nobody believes they
                // recorded something they did not. Both are checked before
                // returning, so one Save names every consent still unconfirmed.
                const whatsAppUnconfirmed =
                  whatsAppChanged && whatsAppDraft === true && !whatsAppAffirmed;
                const medicationHistoryUnconfirmed =
                  medicationHistoryChanged && medicationHistoryDraft === true && !medicationHistoryAffirmed;
                if (whatsAppUnconfirmed) {
                  setWhatsAppError(
                    'Confirm you asked the patient and they agreed, or turn WhatsApp messages back off.',
                  );
                }
                if (medicationHistoryUnconfirmed) {
                  setMedicationHistoryError(
                    'Confirm you asked the patient and they agreed, or turn medication history download off.',
                  );
                }
                if (whatsAppUnconfirmed || medicationHistoryUnconfirmed) return;

                setValidating(true);
                try {
                  const result = await checkCredentials(
                    patientId,
                    form.email.trim(),
                    form.cellPhone.replace(/\D/g, ''),
                    normalizeDialCode(form.cellCountryCode),
                  );
                  if (!result.valid) {
                    setFieldErrors(result.errors);
                    setMasterConflict(result.master ?? null);
                    setValidating(false);
                    return;
                  }
                } catch {
                  setValidating(false);
                  saveMutation.mutate(form);
                  return;
                }
                setValidating(false);
                saveMutation.mutate(form);
              }}
            >
              Save changes
            </Button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Photo Upload */}
          <div className="flex items-center gap-4">
            {patientPhotoUrl ? (
              <img
                src={patientPhotoUrl}
                alt="Patient profile"
                className="h-20 w-20 rounded-full border border-slate-200 object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-xl font-semibold text-slate-500">
                {initials}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setSaveFeedback(null);
                  uploadMutation.mutate(file);
                  event.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={uploadMutation.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload photo
              </Button>
              {uploadMutation.isError && (
                <p className="text-xs text-red-600">
                  {getErrorMessage(uploadMutation.error, 'Photo upload failed.')}
                </p>
              )}
            </div>
          </div>

          {/* Names */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="First Name"
              required
              error={fieldErrors.firstName ?? limitErrors.firstName}
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('first_name')}
                  onApply={(v) => setForm((c) => c ? { ...c, firstName: v } : c)}
                />
              }
            >
              <Input
                value={form.firstName}
                className={fieldErrors.firstName || limitErrors.firstName ? 'border-red-400' : ''}
                onChange={(event) => {
                  setForm((current) => current ? { ...current, firstName: event.target.value } : current);
                  if (fieldErrors.firstName) setFieldErrors((prev) => { const { firstName: _, ...rest } = prev; return rest; });
                }}
              />
            </FormField>
            <FormField
              label="Middle Name"
              error={limitErrors.middleName}
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('middle_name')}
                  onApply={(v) => setForm((c) => c ? { ...c, middleName: v } : c)}
                />
              }
            >
              <Input
                value={form.middleName}
                className={limitErrors.middleName ? 'border-red-400' : ''}
                onChange={(event) => setForm((current) => current ? { ...current, middleName: event.target.value } : current)}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Last Name"
              required
              error={fieldErrors.lastName ?? limitErrors.lastName}
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('last_name')}
                  onApply={(v) => setForm((c) => c ? { ...c, lastName: v } : c)}
                />
              }
            >
              <Input
                value={form.lastName}
                className={fieldErrors.lastName || limitErrors.lastName ? 'border-red-400' : ''}
                onChange={(event) => {
                  setForm((current) => current ? { ...current, lastName: event.target.value } : current);
                  if (fieldErrors.lastName) setFieldErrors((prev) => { const { lastName: _, ...rest } = prev; return rest; });
                }}
              />
            </FormField>
            <FormField
              label="Maiden Name"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('maiden_name')}
                  onApply={(v) => setForm((c) => c ? { ...c, maidenName: v } : c)}
                />
              }
            >
              <Input
                value={form.maidenName}
                onChange={(event) => setForm((current) => current ? { ...current, maidenName: event.target.value } : current)}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Nickname"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('nickname')}
                  onApply={(v) => setForm((c) => c ? { ...c, nickname: v } : c)}
                />
              }
            >
              <Input
                value={form.nickname}
                onChange={(event) => setForm((current) => current ? { ...current, nickname: event.target.value } : current)}
              />
            </FormField>
          </div>

          {/* DOB + Age + Gender */}
          <div className="flex flex-wrap items-end gap-4">
            <FormField
              label="Date of Birth"
              className="w-40"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={dobHistory}
                  onApply={(v) => setForm((c) => c ? { ...c, birthDate: v } : c)}
                />
              }
            >
              <DatePicker
                birthDate
                value={form.birthDate}
                onChange={(value) => setForm((current) => current ? { ...current, birthDate: value } : current)}
              />
            </FormField>
            <span className="pb-2 text-sm font-medium text-slate-600">
              {computeAge(form.birthDate) != null ? `Age: ${computeAge(form.birthDate)}` : ''}
            </span>
            <FormField
              label="Gender"
              className="w-28"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('gender')}
                  onApply={(v) => setForm((c) => c ? { ...c, gender: v } : c)}
                  formatValue={(v) => v === 'M' ? 'Male' : v === 'F' ? 'Female' : v || '(Blank)'}
                />
              }
            >
              <Select
                options={GENDER_OPTIONS}
                value={form.gender}
                onChange={(value) => setForm((current) => current ? { ...current, gender: value ?? '' } : current)}
              />
            </FormField>
          </div>

          {/* Email */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <FormField
                label="Email"
                required
                error={fieldErrors.email}
                labelExtra={
                  <div className="flex items-center gap-1">
                    {detailQuery.data?.patient.emailVerified && <span title="Email Verified" className="flex"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /></span>}
                    <DemographicsHistoryPopover
                      entries={historyFor('email')}
                      onApply={(v) => setForm((c) => c ? { ...c, email: v } : c)}
                    />
                  </div>
                }
              >
                <Input
                  value={form.email}
                  className={fieldErrors.email ? 'border-red-400' : ''}
                  onChange={(event) => {
                    setForm((current) => current ? { ...current, email: event.target.value } : current);
                    if (fieldErrors.email) setFieldErrors((prev) => { const { email: _, ...rest } = prev; return rest; });
                  }}
                />
              </FormField>
              <div className="flex items-center gap-4 pl-0.5">
                <Toggle
                  label="Transactional Email"
                  checked={form.emailOptIn === '1'}
                  onChange={(val) => setForm((current) => current ? { ...current, emailOptIn: val ? '1' : '0' } : current)}
                />
                <DemographicsHistoryPopover
                  entries={historyFor('emailOptIn')}
                  formatValue={onOff}
                  onApply={(v) => setForm((c) => c ? { ...c, emailOptIn: v === '1' ? '1' : '0' } : c)}
                />
                <Toggle
                  label="Marketing Email"
                  checked={form.marketingEmails === '1'}
                  onChange={(val) => {
                    const msg = val
                      ? 'This patient has unsubscribed from marketing emails. They should not be resubscribed without their permission. Do you want to continue?'
                      : 'You are about to unsubscribe this patient from marketing emails. Do you want to continue?';
                    if (!window.confirm(msg)) return;
                    setForm((current) => current ? { ...current, marketingEmails: val ? '1' : '0' } : current);
                  }}
                />
                <DemographicsHistoryPopover
                  entries={historyFor('emailUnsubscribe')}
                  formatValue={subscribedLabel}
                  onApply={(v) => setForm((c) => c ? { ...c, marketingEmails: v === '1' ? '0' : '1' } : c)}
                />
              </div>
            </div>
          </div>

          {/* Cell Phone + Home Phone */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <FormField
                label="Cell Phone"
                required
                error={fieldErrors.cellPhone}
                labelExtra={
                  <div className="flex items-center gap-1">
                    {detailQuery.data?.patient.phoneVerified && <span title="Phone Verified" className="flex"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /></span>}
                    <DemographicsHistoryPopover
                      entries={historyFor('cell_phone')}
                      onApply={(v) => {
                        const raw = v.replace(/\D/g, '');
                        setForm((c) => c ? { ...c, cellPhone: raw } : c);
                      }}
                    />
                  </div>
                }
              >
                <div className="flex items-center gap-1">
                  <DialCodeSelect
                    value={form.cellCountryCode}
                    countries={countryQuery.data ?? []}
                    loading={countryQuery.isLoading}
                    onChange={(code) => {
                      setForm((current) => current ? { ...current, cellCountryCode: code } : current);
                      if (fieldErrors.cellPhone) setFieldErrors((prev) => { const { cellPhone: _, ...rest } = prev; return rest; });
                    }}
                    className="!px-2 !gap-1"
                    wrapperClassName="w-20 shrink-0"
                  />
                  <Input
                    value={formatPhoneAsYouType(form.cellPhone, form.cellCountryCode)}
                    placeholder={phonePlaceholder(form.cellCountryCode)}
                    className={fieldErrors.cellPhone ? 'border-red-400' : ''}
                    onChange={(event) => {
                      const raw = event.target.value.replace(/\D/g, '');
                      setForm((current) => current ? { ...current, cellPhone: raw } : current);
                      if (fieldErrors.cellPhone) setFieldErrors((prev) => { const { cellPhone: _, ...rest } = prev; return rest; });
                    }}
                    wrapperClassName="flex-1"
                  />
                </div>
              </FormField>
              <div className="flex items-center gap-4 pl-0.5">
                <Toggle
                  label="Transactional SMS"
                  checked={form.smsOptIn === '1'}
                  onChange={(val) => setForm((current) => current ? { ...current, smsOptIn: val ? '1' : '0' } : current)}
                />
                <DemographicsHistoryPopover
                  entries={historyFor('smsOptIn')}
                  formatValue={onOff}
                  onApply={(v) => setForm((c) => c ? { ...c, smsOptIn: v === '1' ? '1' : '0' } : c)}
                />
                <Toggle
                  label="Marketing SMS"
                  checked={form.marketingSms === '1'}
                  onChange={(val) => {
                    const msg = val
                      ? 'This patient has unsubscribed from marketing SMS. They should not be resubscribed without their permission. Do you want to continue?'
                      : 'You are about to unsubscribe this patient from marketing SMS. Do you want to continue?';
                    if (!window.confirm(msg)) return;
                    setForm((current) => current ? { ...current, marketingSms: val ? '1' : '0' } : current);
                  }}
                />
                <DemographicsHistoryPopover
                  entries={historyFor('smsUnsubscribe')}
                  formatValue={subscribedLabel}
                  onApply={(v) => setForm((c) => c ? { ...c, marketingSms: v === '1' ? '0' : '1' } : c)}
                />
              </div>
              {/* WhatsApp sits with the SMS pair because it goes to this same
                  cell number, and because this is where staff already come to
                  change what a patient may be contacted about — which is the
                  whole reason it moved here from inside the WhatsApp composer,
                  where it was reachable only when already blocked.

                  The info button is a SIBLING of the Toggle, not inside it:
                  Toggle wraps its switch in a <label> containing a <button>,
                  and nesting a button inside a button is invalid. */}
              {whatsAppOn && (
                <div className="space-y-1.5 pl-0.5">
                  <div className="flex items-center gap-1.5">
                    <Toggle
                      label="WhatsApp messages"
                      checked={whatsAppValue}
                      onChange={(val) => {
                        setWhatsAppError(null);
                        setWhatsAppDraft(val);
                        if (!val) setWhatsAppAffirmed(false);
                      }}
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          tabIndex={-1}
                          aria-label="How WhatsApp messaging works"
                          className="inline-flex text-slate-400 hover:text-slate-600"
                        >
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[300px]">{WHATSAPP_RULE}</TooltipContent>
                    </Tooltip>
                  </div>

                  {/* The attestation, revealed by turning it on. A separate act
                      from the toggle on purpose: this is saved as a named
                      person's word, so a single flick of a switch should not be
                      able to assert it. */}
                  {whatsAppValue && whatsAppSaved !== true && (
                    <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={whatsAppAffirmed}
                        onChange={(e) => {
                          setWhatsAppAffirmed(e.target.checked);
                          if (e.target.checked) setWhatsAppError(null);
                        }}
                      />
                      <span>
                        I asked the patient and they agreed to be contacted on WhatsApp. Your name
                        and today's date are saved with this.
                      </span>
                    </label>
                  )}

                  {whatsAppError && (
                    <p className="text-xs text-red-600">{whatsAppError}</p>
                  )}
                </div>
              )}
            </div>
            <FormField
              label="Home Phone"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('home_phone')}
                  onApply={(v) => {
                    const raw = v.replace(/\D/g, '');
                    setForm((c) => c ? { ...c, homePhone: raw } : c);
                  }}
                />
              }
            >
              <div className="flex items-center gap-1">
                <DialCodeSelect
                  value={form.phoneCountryCode}
                  countries={countryQuery.data ?? []}
                  loading={countryQuery.isLoading}
                  onChange={(code) => setForm((current) => current ? { ...current, phoneCountryCode: code } : current)}
                  className="!px-2 !gap-1"
                  wrapperClassName="w-20 shrink-0"
                />
                <Input
                  value={formatPhoneAsYouType(form.homePhone, form.phoneCountryCode)}
                  placeholder={phonePlaceholder(form.phoneCountryCode)}
                  onChange={(event) => {
                    const raw = event.target.value.replace(/\D/g, '');
                    setForm((current) => current ? { ...current, homePhone: raw } : current);
                  }}
                  wrapperClassName="flex-1"
                />
              </div>
            </FormField>
          </div>

          {/* Facility */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Facility">
              <Select
                options={facilityOptions}
                value={form.facilityID}
                loading={facilityQuery.isLoading}
                onChange={(value) => setForm((current) => current ? { ...current, facilityID: value ?? '' } : current)}
              />
            </FormField>
          </div>

          {/* Address (full width) */}
          <FormField
            label="Address"
            error={limitErrors.address}
            labelExtra={
              <DemographicsHistoryPopover
                entries={historyFor('address')}
                onApply={(v) => setForm((c) => c ? { ...c, address: v } : c)}
              />
            }
          >
            <Input
              value={form.address}
              className={limitErrors.address ? 'border-red-400' : ''}
              onChange={(event) => setForm((current) => current ? { ...current, address: event.target.value } : current)}
            />
          </FormField>

          {/* City, State, Zip, Country */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <FormField
              label="City"
              error={limitErrors.city}
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('city')}
                  onApply={(v) => setForm((c) => c ? { ...c, city: v } : c)}
                />
              }
            >
              <Input
                value={form.city}
                className={limitErrors.city ? 'border-red-400' : ''}
                onChange={(event) => setForm((current) => current ? { ...current, city: event.target.value } : current)}
              />
            </FormField>
            <FormField
              label="State"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('state')}
                  onApply={(v) => setForm((c) => c ? { ...c, state: v } : c)}
                />
              }
            >
              <Input
                value={form.state}
                onChange={(event) => setForm((current) => current ? { ...current, state: event.target.value } : current)}
              />
            </FormField>
            <FormField
              label="Zip"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('zip')}
                  onApply={(v) => setForm((c) => c ? { ...c, zip: v } : c)}
                />
              }
            >
              <Input
                value={form.zip}
                onChange={(event) => setForm((current) => current ? { ...current, zip: event.target.value } : current)}
              />
            </FormField>
            <FormField
              label="Country"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('country')}
                  onApply={(v) => setForm((c) => c ? { ...c, countryID: v } : c)}
                />
              }
            >
              <Select
                options={countryOptions}
                searchable
                value={form.countryID}
                loading={countryQuery.isLoading}
                onChange={(value) => setForm((current) => current ? { ...current, countryID: value ?? '' } : current)}
              />
            </FormField>
          </div>

          {/* Language + Interpreter */}
          <div className="flex flex-wrap items-end gap-4">
            <FormField
              label="Language"
              labelExtra={
                <DemographicsHistoryPopover
                  entries={historyFor('language')}
                  onApply={(v) => setForm((c) => c ? { ...c, languageId: v } : c)}
                  formatValue={formatLanguageValue}
                />
              }
            >
              <Select
                options={languageOptions}
                value={form.languageId}
                loading={languageQuery.isLoading}
                onChange={(value) => setForm((current) => current ? { ...current, languageId: value ?? '' } : current)}
                wrapperClassName="w-44"
              />
            </FormField>
            <div className="flex items-center gap-2 pb-2">
              <Toggle
                label="Interpreter Needed"
                checked={form.interpreterNeeded === '1'}
                onChange={(val) => setForm((current) => current ? { ...current, interpreterNeeded: val ? '1' : '0' } : current)}
              />
              <DemographicsHistoryPopover
                entries={historyFor('interpreterNeeded')}
                onApply={(v) => setForm((c) => c ? { ...c, interpreterNeeded: v === '1' ? '1' : '0' } : c)}
                formatValue={formatInterpreterValue}
              />
            </div>
          </div>

          {/* Medication history consent has a row of its own. The consents
              above each sit with the contact channel they govern; this one
              governs none. It is whether ScriptSure may download the patient's
              medication history from pharmacies and insurers.

              The toggle appears once the saved answer has loaded. Before that
              a change has nothing to be compared with, so the save would drop
              it without a word. */}
          {dawOn && (medicationHistorySaved !== null ? (
            <div className="space-y-1.5 pl-0.5">
              <Toggle
                label="Patient consents to download medication history"
                checked={medicationHistoryValue}
                onChange={(val) => {
                  setMedicationHistoryError(null);
                  setMedicationHistoryDraft(val);
                  if (!val) setMedicationHistoryAffirmed(false);
                }}
              />

              {/* The attestation, revealed by turning it on, for the same
                  reason as WhatsApp's. */}
              {medicationHistoryValue && medicationHistorySaved !== true && (
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={medicationHistoryAffirmed}
                    onChange={(e) => {
                      setMedicationHistoryAffirmed(e.target.checked);
                      if (e.target.checked) setMedicationHistoryError(null);
                    }}
                  />
                  <span>
                    I asked the patient and they agreed to let us download their medication
                    history. Your name and today's date are kept with this.
                  </span>
                </label>
              )}

              {medicationHistoryError && (
                <p className="text-xs text-red-600">{medicationHistoryError}</p>
              )}
            </div>
          ) : medicationHistoryQuery.isError ? (
            <p className="text-xs text-red-600">
              The medication history consent couldn't be loaded. Press Cancel, then Edit, to try again.
            </p>
          ) : null)}
        </div>
      )}
    </Modal>
  );
}
